import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { TrendingUp, TrendingDown, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";

export function ReadinessPanel() {
  const [r, setR] = useState(null);
  const navigate = useNavigate();
  const { t } = useLang();
  useEffect(() => { api.get("/readiness").then(({ data }) => setR(data)).catch(() => setR(false)); }, []);
  if (!r || !r.target) return null;
  const delta = r.delta_month || 0;
  const chart = (r.monthly.length > 1 ? r.monthly.map((m) => ({ x: m.month.slice(5), y: m.score })) : r.history.map((h) => ({ x: h.date.slice(5), y: h.score })));
  const color = (s) => (s >= 70 ? "bg-emerald-500" : s >= 45 ? "bg-indigo-500" : "bg-rose-400");
  return (
    <div className="rounded-3xl border border-border bg-card p-6" data-testid="readiness-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-600">{t("careerReadiness")}</div>
          <div className="mt-1 font-display text-5xl font-extrabold leading-none" data-testid="readiness-score">{r.score}%</div>
          <div className="mt-2 text-sm font-bold uppercase tracking-wide text-indigo-600">{t("target")}: {r.target}</div>
        </div>
        {delta !== 0 && (
          <span data-testid="readiness-panel-delta" className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${delta > 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
            {delta > 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />} {delta > 0 ? "+" : ""}{delta}% {t("thisMonth")}
          </span>
        )}
      </div>

      <div className="mt-5 space-y-3" data-testid="readiness-breakdown">
        {r.components.map((c, i) => (
          <div key={c.key} data-testid={`readiness-${c.key}`}>
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold">{c.label}</span>
              <span className="font-mono font-bold">{c.score}%</span>
            </div>
            <div className="mt-1 h-2 overflow-hidden rounded-full bg-secondary">
              <motion.div className={`h-full rounded-full ${color(c.score)}`} initial={{ width: 0 }} animate={{ width: `${c.score}%` }} transition={{ duration: 0.8, delay: i * 0.05 }} />
            </div>
          </div>
        ))}
      </div>

      {r.biggest_gap && (
        <button onClick={() => navigate(r.biggest_gap.kind === "proof" ? "/app/proof" : r.biggest_gap.kind === "interview" ? "/app/interview" : "/app/missions")}
          data-testid="readiness-work-on-gap" className="mt-5 flex w-full items-center justify-between rounded-2xl bg-rose-50 px-4 py-3 text-left">
          <div><div className="text-[10px] font-bold uppercase tracking-widest text-rose-500">{t("biggestGap")}</div><div className="font-bold">{r.biggest_gap.label} · {r.biggest_gap.score}%</div></div>
          <span className="flex items-center gap-1 text-sm font-bold text-rose-600">{t("workOnIt")} <ArrowRight className="h-4 w-4" /></span>
        </button>
      )}

      {chart.length > 1 && (
        <div className="mt-5" data-testid="readiness-history-chart">
          <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{t("readinessHistory")}</div>
          <ResponsiveContainer width="100%" height={140}>
            <LineChart data={chart} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
              <XAxis dataKey="x" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v) => `${v}%`} />
              <Line type="monotone" dataKey="y" stroke="#10B981" strokeWidth={3} dot={{ r: 4, fill: "#10B981" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <p className="mt-3 text-[11px] text-muted-foreground">{r.disclaimer}</p>
    </div>
  );
}
